<template>
  <LinkWrapper
    v-if="operatorLogo"
    :href="operatorLink"
    :class="[classes.link, logoClass]"
    target="_blank"
    @click="handleClick"
  >
    <img
      :src="operatorLogo"
      :class="[classes.image, 'operator-logo']"
      @load="effectTrigger"
    />
    <span
      v-if="leagueLogoSrc"
      data-testid="leagueLogoSeparator"
      :class="classes.separator"
    ></span>
    <img
      v-if="leagueLogoSrc"
      data-testid="leagueImageIn"
      :src="leagueLogoSrc"
      :class="[classes.leagueImage, classes.leagueImgWithOperLogo]"
    />
  </LinkWrapper>
  <span v-else :class="[classes.link, logoClass]">
    <img
      v-if="leagueLogoSrc"
      data-testid="leagueImageOut"
      :src="leagueLogoSrc"
      :class="classes.leagueImage"
    />
  </span>
</template>

<script lang="ts">
import { defineComponent, computed } from "vue";
import { createUseStyles } from "@fresh8/vue-jss/src";
import { useLayoutEffect } from "@fresh8/vue-utils/src/dimensionSync";
import * as generateLinks from "@fresh8/ad-data-composition/src/lib/generateLinks";
import LinkWrapper from "./LinkWrapper.vue";
import {
  AdTagContext,
  BrandContext,
  injectAdTag,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";
import {
  getLeagueLogoSrc,
  injectBetData
} from "@fresh8/ad-data-composition/src/betData";
import { useAnalytics } from "../hooks/useAnalytics";
import {
  injectTemplateSize,
  LARGE_LEADERBOARD,
  LARGE_MPU,
  LEADERBOARD,
  SKYSCRAPER
} from "@fresh8/ad-data-composition/src/useTemplateSize";

/**
 * League logo custom config
 */
const customSizes = {
  [LARGE_LEADERBOARD.name]: { linkMaxSize: "90px" },
  [SKYSCRAPER.name]: { linkMaxSize: "90px !important" },
  [LARGE_MPU.name]: { leagueMaxSize: "35px", leagueMarginLeft: "8px" },
  [LEADERBOARD.name]: { linkMaxSize: "90px" }
};

const useStyles = createUseStyles({
  link: {
    height: "100%",
    flex: 1,
    justifyContent: "flex-start",
    maxWidth: props =>
      customSizes[props.layout || ""]?.linkMaxSize ||
      ((undefined as unknown) as string)
  },
  image: {
    height: "100%",
    objectFit: "contain",
    maxHeight: "inherit",
    maxWidth: "inherit",
    display: "block",
    margin: "auto 0"
  },
  leagueImage: {
    height: "100%",
    maxWidth: props => customSizes[props.layout || ""]?.leagueMaxSize || "21px",
    objectFit: "contain",
    maxHeight: "inherit",
    display: "block",
    marginLeft: props =>
      customSizes[props.layout || ""]?.leagueMarginLeft || "4px"
  },
  leagueImgWithOperLogo: { transform: "scale(1.3)" },
  separator: {
    height: "100%",
    maxHeight: "inherit",
    borderRight: props => `0.5px solid ${props.marketBorderColor}`,
    margin: "auto 4px"
  }
});

export default defineComponent({
  name: "OperatorLogo",
  components: {
    LinkWrapper
  },
  props: { class: { type: String, default: "", required: false } },
  setup(props) {
    const leagueLogoSrc = getLeagueLogoSrc();
    const { operatorLogos, adTemplateBranding } =
      injectBrand() || ({} as BrandContext);
    const templateContext = injectTemplateSize();
    const computedStyles = computed(() => ({
      ...adTemplateBranding.value,
      layout: leagueLogoSrc && templateContext?.size.value.name
    }));
    const classes = useStyles(computedStyles);
    const betData = injectBetData();
    const adTag = injectAdTag() || ({} as AdTagContext);
    const effectTrigger = useLayoutEffect();
    const { handleClick } = useAnalytics({}, "Operator Logo");

    const url = computed(() => {
      const campaignTarget = adTag?.selectedCampaign?.value?.targetUrl;
      const fallback =
        adTag?.adTag?.value?.tracking[
          adTag.selectedOperator?.value?._id as string
        ]?.fallbackUrl;
      if (campaignTarget && campaignTarget !== "") {
        return generateLinks.generate(campaignTarget, {
          affiliateId: betData?.affiliateId.value,
          clickUrl: betData?.clickUrl.value,
          operatorKey: betData?.operator.value?.name || ""
        });
      } else if (fallback && fallback !== "") {
        return generateLinks.generate(fallback, {
          affiliateId: betData?.affiliateId.value,
          clickUrl: betData?.clickUrl.value,
          operatorKey: betData?.operator.value?.name || ""
        });
      }
      return betData?.links.value.operator;
    });

    return {
      logoClass: computed(() => props.class),
      classes,
      operatorLogo: computed(() => {
        return (
          adTag.operator.value &&
          (
            operatorLogos.value?.[adTag.operator.value._id || ""] ||
            operatorLogos.value?.[adTag.operator.value.key]
          )?.desktop?.binary
        );
      }),
      leagueLogoSrc,
      operatorLink: computed(() => url.value),
      effectTrigger,
      handleClick
    };
  }
});
</script>

<style></style>
